import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { Article } from "@/lib/types";
import { useAuth } from "@/context/AuthContext";
import { TxTitle, useArticlePath } from "@/lib/i18n";
import { CAT_COLORS, catLabel } from "../morestories/LiveFeed";
import { homeTrack } from "../shared";

const FALLBACK = "/thumbs/market_globe.jpg";

export const ProgressBar = ({ pct, className = "" }: { pct: number; className?: string }) => {
  const [w, setW] = useState(0);
  useEffect(() => { const t = requestAnimationFrame(() => setW(pct)); return () => cancelAnimationFrame(t); }, [pct]);
  return (
    <span role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`${pct}% read`}
      className={`block h-[4px] w-full rounded-full bg-[#151a26] overflow-hidden ${className}`}>
      <span className="block h-full rounded-full bg-gradient-to-r from-[#10D7F2] to-[#A955FF] shadow-[0_0_7px_rgba(16,215,242,0.35)] motion-safe:transition-[width] motion-safe:duration-[550ms] motion-safe:ease-out"
        style={{ width: `${w}%` }} />
    </span>
  );
};

const rowShell = (emphasized: boolean) =>
  `group flex flex-wrap sm:flex-nowrap items-center gap-x-3.5 gap-y-3 rounded-[10px] border p-2.5 sm:pr-4 motion-safe:transition-colors motion-safe:duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A955FF]/60 ${
    emphasized
      ? "border-[#A955FF]/35 bg-[#A955FF]/[0.05] hover:bg-[#A955FF]/[0.08]"
      : "border-[#242A39] bg-[#080B14] hover:bg-[#0b0f1c] hover:border-[#30364A]"}`;

const RowMedia = ({ a }: { a: any }) => (
  <img src={a.hero_image_url || FALLBACK} alt="" loading="lazy" width={170} height={74}
    onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
    className="w-[112px] h-[64px] sm:w-[148px] sm:h-[72px] xl:w-[170px] xl:h-[74px] object-cover rounded-[7px] shrink-0 border border-white/[0.05] motion-safe:transition-transform motion-safe:duration-200 group-hover:scale-[1.01]" />
);

const RowContent = ({ a, lastRead }: { a: any; lastRead?: string }) => {
  const cat = a.categories?.[0];
  const desc = a.summary_ai || a.summary;
  return (
    <span className="min-w-0 flex-1 basis-[52%] sm:basis-auto">
      <span className="font-mono text-[9px] font-bold uppercase tracking-[0.16em]"
        style={{ color: CAT_COLORS[cat || ""] || "#A955FF" }}>{catLabel(cat)}</span>
      <span className="line-clamp-1 font-news text-[14px] sm:text-[15px] font-semibold text-[#F5F7FB] group-hover:text-white leading-snug mt-[3px] motion-safe:transition-colors">
        <TxTitle slug={a.slug} title={a.title} />
      </span>
      {desc && (
        <span className="hidden sm:[display:-webkit-box] line-clamp-1 text-[10.5px] text-[#929AAC] leading-snug mt-[3px]">
          {desc}
        </span>
      )}
      <span className="block font-mono text-[8.5px] uppercase tracking-[0.14em] text-[#5d6577] mt-[5px]">
        {timeAgo(lastRead || a.published_at)}{a.read_minutes ? ` · ${a.read_minutes} min read` : ""}
      </span>
    </span>
  );
};

const ResumeRow = ({ a, first, premiumUser }: { a: any; first: boolean; premiumUser: boolean }) => {
  const toArticle = useArticlePath();
  const gated = a.access_level === "premium" && !premiumUser;
  const dest = gated ? `/premium?read=${a.slug}` : `${toArticle(a.slug)}?resume=1`;
  return (
    <li className="relative pl-8 sm:pl-9 list-none">
      <TimelineNode first={first} />
      <Link to={dest} data-testid={`resume-row-${a.slug}`}
        onClick={() => homeTrack("jump_back_in", gated ? `paywall_${a.slug}` : a.slug)}
        className={rowShell(first)}>
        <RowMedia a={a} />
        <RowContent a={a} lastRead={a.last_read_at} />
        <span className="flex items-center gap-3 sm:gap-3.5 w-full sm:w-auto sm:shrink-0">
          <span className="flex-1 sm:flex-none sm:w-[112px] min-w-0">
            <span className="block font-mono text-[10px] font-bold uppercase tracking-[0.14em] text-[#10D7F2]"
              data-testid={`resume-progress-${a.slug}`}>{a.progress_percent}% read</span>
            <ProgressBar pct={a.progress_percent} className="mt-[5px]" />
            {a.minutes_left != null && (
              <span className="block font-mono text-[8.5px] uppercase tracking-[0.14em] text-[#5d6577] mt-[5px]">
                {a.minutes_left} min left
              </span>
            )}
          </span>
          <span data-testid={`resume-btn-${a.slug}`}
            className={`inline-flex items-center gap-1.5 rounded-[7px] border px-3 py-[7px] text-[11px] font-bold whitespace-nowrap shrink-0 motion-safe:transition-colors ${gated
              ? "border-[#A955FF]/45 bg-[#12081f]/70 text-[#C08BFF] group-hover:border-[#A955FF] group-hover:text-white"
              : "border-[#10D7F2]/40 bg-[#071018]/70 text-[#10D7F2] group-hover:border-[#10D7F2] group-hover:text-white"}`}>
            <Play className="h-3 w-3 fill-current" aria-hidden />
            {gated ? "Resume with Premium" : "Resume"}
          </span>
        </span>
      </Link>
    </li>
  );
};

const TimelineNode = ({ first }: { first: boolean }) => (
  <span aria-hidden className={first
    ? "nl-node-pulse absolute left-0 top-1/2 -translate-y-1/2 flex h-[26px] w-[26px] items-center justify-center rounded-full border border-[#A955FF]/55 bg-[#A955FF]/15 text-[#C08BFF] z-[1]"
    : "absolute left-[8px] top-1/2 -translate-y-1/2 h-[10px] w-[10px] rounded-full border border-[#3a3f52] bg-[#0d0f16] z-[1]"}>
    {first && <Play className="h-[11px] w-[11px] fill-current" />}
  </span>
);

const TimelineRail = () => (
  <span aria-hidden className="absolute left-[12.5px] top-5 bottom-5 w-px bg-gradient-to-b from-[#A955FF]/45 via-[#A955FF]/20 to-transparent" />
);

export const ResumeTimeline = ({ items }: { items: any[] }) => {
  const { user } = useAuth();
  return (
    <ol className="relative m-0 p-0 space-y-2.5 lg:h-full lg:flex lg:flex-col lg:justify-between" data-testid="resume-timeline">
      <TimelineRail />
      {items.map((a, i) => (
        <ResumeRow key={a.slug} a={a} first={i === 0} premiumUser={!!user?.is_premium} />
      ))}
    </ol>
  );
};

/* Empty/guest variant — same row framework, no fabricated progress. */
export const CuratedList = ({ items, label }: { items: Article[]; label: string }) => {
  const toArticle = useArticlePath();
  if (!items.length) return null;
  return (
    <div data-testid="jump-back-in-curated" className="lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
      <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.18em] text-[#5d6577] mb-2.5 pl-8 sm:pl-9">{label}</p>
      <ul className="relative m-0 p-0 space-y-2.5 lg:flex-1 lg:flex lg:flex-col lg:justify-between">
        <TimelineRail />
        {items.map((a) => (
          <li key={a.slug} className="relative pl-8 sm:pl-9 list-none">
            <span aria-hidden className="absolute left-[8px] top-1/2 -translate-y-1/2 h-[10px] w-[10px] rounded-full border border-[#3a3f52] bg-[#0d0f16] z-[1]" />
            <Link to={toArticle(a.slug)} data-testid={`curated-row-${a.slug}`}
              onClick={() => homeTrack("jump_back_in", `curated_${a.slug}`)}
              className={rowShell(false)}>
              <RowMedia a={a} />
              <RowContent a={a} />
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-[7px] border border-[#242A39] px-3 py-[7px] text-[11px] font-bold text-[#929AAC] group-hover:border-[#10D7F2]/50 group-hover:text-[#10D7F2] whitespace-nowrap shrink-0 motion-safe:transition-colors">
                Read <ArrowRight className="h-3 w-3" aria-hidden />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const TimelineSkeleton = () => (
  <div className="space-y-2.5 pl-8 sm:pl-9" aria-hidden data-testid="resume-skeleton">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3.5 rounded-[10px] border border-[#1a2030] bg-[#080B14] p-2.5">
        <span className="w-[148px] h-[72px] rounded-[7px] bg-white/[0.04] animate-pulse shrink-0" />
        <span className="flex-1 space-y-2">
          <span className="block h-2 w-20 rounded bg-white/[0.05] animate-pulse" />
          <span className="block h-3 w-3/4 rounded bg-white/[0.06] animate-pulse" />
          <span className="block h-2 w-32 rounded bg-white/[0.04] animate-pulse" />
        </span>
        <span className="hidden sm:block w-[112px] h-7 rounded bg-white/[0.04] animate-pulse shrink-0" />
      </div>
    ))}
  </div>
);
